// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage } from '@/api/error-utils';
import { AttachableCharge } from '@/billing/api/get-attachable-charges';
import { InvoiceDetail, InvoiceLineItem } from '@/billing/api/types';
import {
  NewLineItemEntry,
  UpdateLineItemEntry,
} from '@/billing/api/update-line-items';
import { AddLineItemDialog } from '@/billing/components/add-line-item-dialog';
import { EditableLineItemRow } from '@/billing/components/editable-line-item-row';
import {
  ReadOnlySkeletonRow,
  SkeletonRow,
} from '@/billing/components/line-item-skeleton-rows';
import { ReadOnlyLineItemRow } from '@/billing/components/read-only-line-item-row';
import { useUpdateLineItems } from '@/billing/hooks/use-update-line-items';
import { NewChargeFormData } from '@/billing/schemas/new-charge-form-schema';
import {
  LineItemsFormState,
  isLineItemDragData,
  lineItemsFormSchema,
  parseFormAmount,
  queuedAdhocToFormData,
  queuedAttachToFormData,
  toFormData,
  toNewLineItemEntry,
  toUpdateEntry,
} from '@/billing/utilities/line-item-helpers';
import { KnownErrorSnackbar } from '@/components/known-error-snackbar';
import { SupportSnackbar } from '@/components/support-snackbar';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import {
  AddRounded,
  CheckRounded,
  CloseRounded,
  EditRounded,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import { useDeferredMount } from '@/hooks/use-deferred-mount';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Divider,
  Fade,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useBlocker } from '@tanstack/react-router';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, useFieldArray, useForm, useWatch } from 'react-hook-form';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EditableLineItemTableProps {
  invoice: InvoiceDetail;
  lineItems: InvoiceLineItem[];
  lineItemsLoading: boolean;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function EditableLineItemTable(props: EditableLineItemTableProps) {
  const { invoice, lineItems, lineItemsLoading } = props;
  const isDraft = invoice.status === 'DRAFT';
  const { showToast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const { isReady } = useDeferredMount(isEditing);

  const { control, reset, handleSubmit, setValue, formState } =
    useForm<LineItemsFormState>({
      defaultValues: { lineItems: [] },
      resolver: zodResolver(lineItemsFormSchema),
      mode: 'onBlur',
    });

  const { fields, move, remove, append } = useFieldArray({
    control,
    name: 'lineItems',
  });

  // Watch the form state to derive the set of pending charge keys (queued
  // adds that haven't been persisted yet). Passed to the dialog so the
  // picker filters them out — the same charge can't be queued twice. The
  // BE picker already excludes charges bound to any existing line item.
  const watchedLineItems = useWatch({ control, name: 'lineItems' });
  const queuedChargeKeys = useMemo<ReadonlySet<string>>(() => {
    const out = new Set<string>();
    for (const li of watchedLineItems ?? []) {
      if (li.pendingChargeKey !== null) {
        out.add(li.pendingChargeKey);
      }
    }
    return out;
  }, [watchedLineItems]);

  const totalEditRows = lineItems.length;

  const [showSupportSnackbar, setShowSupportSnackbar] = useState(false);
  const [knownErrorMessage, setKnownErrorMessage] = useState<string | null>(
    null
  );
  const mutation = useUpdateLineItems({
    invoiceKey: invoice.key,
    onSuccess: () => {
      setIsEditing(false);
      setIsSaving(false);
    },
    onError: (apiError) => {
      setIsSaving(false);
      if (apiError.apiStatusCode === ERROR_CODE_MAPPING.plan_limit_exceeded) {
        return;
      }

      // Mixed-currency rejection is a user-facing validation error — keep the
      // editor open and surface the backend message inline. Everything else
      // falls back to the support snackbar.
      if (
        apiError.apiStatusCode === ERROR_CODE_MAPPING.invoice_mixed_currency
      ) {
        setKnownErrorMessage(getUserFacingErrorMessage(apiError));
        return;
      }
      setShowSupportSnackbar(true);
    },
  });

  const handleStartEdit = useCallback(() => {
    reset({
      lineItems: lineItems.map((li) => toFormData(li, invoice.currency)),
    });
    setIsEditing(true);
  }, [lineItems, invoice.currency, reset]);

  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Block in-app navigation while edit mode is active and the form has
  // pending changes. Without this, clicking a sidebar link / browser back
  // would silently abandon the user's edits.
  const navigationBlocker = useBlocker({
    condition: isEditing && formState.isDirty,
  });

  const forceCancel = useCallback(() => {
    setShowUnsavedDialog(false);
    setIsEditing(false);
    reset({ lineItems: [] });
  }, [reset]);

  // Cancel asks for confirmation when the user has queued additions,
  // removed rows, reordered, or edited any field. formState.isDirty
  // catches all three since useFieldArray mutations also mark dirty.
  const attemptCancel = useCallback(() => {
    if (formState.isDirty) {
      setShowUnsavedDialog(true);
      return;
    }
    forceCancel();
  }, [formState.isDirty, forceCancel]);

  // Append a queued attach to the edit-mode field array. The row stays in
  // form state with a null key + a pendingChargeKey until the next Save
  // persists everything in one transaction.
  const handleAddLineItem = useCallback(
    (charge: AttachableCharge, billingEligibleDate: DateTime) => {
      append(
        queuedAttachToFormData(charge, billingEligibleDate, invoice.currency)
      );
    },
    [append, invoice.currency]
  );

  const handleAddAdhoc = useCallback(
    (data: NewChargeFormData) => {
      append(queuedAdhocToFormData(data, invoice.currency));
    },
    [append, invoice.currency]
  );

  const handleSave = useMemo(
    () =>
      handleSubmit(
        (formData) => {
          setIsSaving(true);
          const updateEntries: UpdateLineItemEntry[] = [];
          const newEntries: NewLineItemEntry[] = [];
          formData.lineItems.forEach((li, index) => {
            if (li.adhocSpec !== null) {
              newEntries.push(toNewLineItemEntry(li, index, invoice.currency));
              return;
            }
            updateEntries.push(toUpdateEntry(li, index));
          });
          mutation.mutate({
            invoiceKey: invoice.key,
            lineItems: updateEntries,
            newLineItems: newEntries,
          });
        },
        (errors) => {
          Sentry.captureMessage('line item edit-mode validation blocked save', {
            level: 'warning',
            extra: { errors: JSON.stringify(errors) },
          });
          showToast({
            severity: 'error',
            element: (
              <span>
                One or more line items have errors. Fix them and try again.
              </span>
            ),
            duration: 5000,
          });
        }
      ),
    [handleSubmit, invoice.key, invoice.currency, mutation, showToast]
  );

  // Drag-and-drop reorder — swaps rows immediately on drag-over for
  // instant visual feedback (not on drop).
  const dragSourceIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    return monitorForElements({
      onDragStart: ({ source }) => {
        const sourceData = source.data;
        if (isLineItemDragData(sourceData)) {
          dragSourceIndexRef.current = sourceData.index;
        }
      },
      onDrop: () => {
        dragSourceIndexRef.current = null;
      },
    });
  }, [isEditing]);

  return (
    <Paper
      variant="outlined"
      sx={isEditing ? { overflow: 'visible' } : undefined}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={
          isEditing
            ? {
                px: 3,
                py: 2,
                position: 'sticky',
                top: 0,
                zIndex: 3,
                backgroundColor: 'background.paper',
              }
            : { px: 3, py: 2 }
        }
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Line items
        </Typography>
        {isDraft && !isEditing && (
          <Button
            size="small"
            variant="contained"
            startIcon={<EditRounded />}
            onClick={handleStartEdit}
          >
            Edit
          </Button>
        )}
        {isEditing && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckRounded />}
              onClick={handleSave}
              disabled={isSaving}
            >
              Save
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CloseRounded />}
              onClick={attemptCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </Stack>

      {/* ---- Idle: read-only table ---- */}
      {!isEditing && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }}>#</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Service period</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Unit price</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Line total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItemsLoading ? (
                Array.from({ length: 5 }, (_, i) => (
                  <ReadOnlySkeletonRow key={`ro-skeleton-${i}`} />
                ))
              ) : lineItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No line items
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                lineItems.map((li, index) => (
                  <ReadOnlyLineItemRow
                    key={li.id}
                    lineItem={li}
                    index={index}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ---- Editing: skeleton until the deferred mount is ready, then the
           real form rows fade in. NOTE: Column structure must stay in sync
           with SkeletonRow and EditableLineItemRow. Update all three when
           changing columns. */}
      {isEditing && (
        <TableContainer sx={{ overflow: 'visible' }}>
          <Table
            size="small"
            sx={{ tableLayout: 'fixed', '& td, & th': { px: 1.5 } }}
          >
            <TableHead
              sx={{
                '& th': {
                  position: 'sticky',
                  top: 56,
                  zIndex: 2,
                  backgroundColor: 'background.paper',
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell>Description</TableCell>
                <TableCell sx={{ width: 215 }}>Service period</TableCell>
                <TableCell align="right" sx={{ width: 95 }}>
                  Qty
                </TableCell>
                <TableCell align="right" sx={{ width: 135 }}>
                  Unit price
                </TableCell>
                <TableCell align="right" sx={{ width: 135 }}>
                  Amount
                </TableCell>
                <TableCell align="right" sx={{ width: 135 }}>
                  Tax
                </TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  Line total
                </TableCell>
                <TableCell sx={{ width: 48, pr: 2 }} />
              </TableRow>
            </TableHead>
            {!isReady && (
              <TableBody>
                {Array.from({ length: totalEditRows }, (_, i) => (
                  <SkeletonRow key={`skeleton-${i}`} />
                ))}
              </TableBody>
            )}
            {isReady && (
              <Fade in timeout={200}>
                <TableBody>
                  {fields.map((field, index) => (
                    <EditableLineItemRow
                      key={field.id}
                      index={index}
                      control={control}
                      setValue={setValue}
                      onSwap={move}
                      onRemove={remove}
                      dragSourceIndexRef={dragSourceIndexRef}
                      currencyCode={invoice.currency}
                    />
                  ))}
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddRounded />}
                        onClick={() => setIsAddOpen(true)}
                      >
                        Add line item
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Fade>
            )}
          </Table>
        </TableContainer>
      )}

      {/* Totals — visible while idle and once the editor is ready. While
           editing, LiveTotalsFooter subscribes to lineItems itself so the
           parent does not re-render on every keystroke. */}
      {!isEditing && (
        <StaticTotalsFooter
          subtotal={invoice.subtotal.amount}
          taxTotal={invoice.taxTotal.amount}
          total={invoice.total.amount}
          currencyCode={invoice.currency}
        />
      )}
      {isEditing && isReady && (
        <LiveTotalsFooter control={control} currencyCode={invoice.currency} />
      )}
      <AddLineItemDialog
        invoiceKey={invoice.key}
        currencyCode={invoice.currency}
        project={invoice.project}
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        queuedChargeKeys={queuedChargeKeys}
        onAdd={handleAddLineItem}
        onAddAdhoc={handleAddAdhoc}
      />
      <UnsavedChangesDialog
        isOpen={showUnsavedDialog}
        onCancel={() => setShowUnsavedDialog(false)}
        onProceed={forceCancel}
      />
      <UnsavedChangesDialog
        isOpen={navigationBlocker.status === 'blocked'}
        onCancel={navigationBlocker.reset}
        onProceed={navigationBlocker.proceed}
      />
      <SupportSnackbar
        isOpen={showSupportSnackbar}
        onClose={() => setShowSupportSnackbar(false)}
        message="Could not save the line item edits."
      />
      <KnownErrorSnackbar
        isOpen={knownErrorMessage !== null}
        onClose={() => setKnownErrorMessage(null)}
        message={knownErrorMessage ?? ''}
      />
    </Paper>
  );
}

interface StaticTotalsFooterProps {
  subtotal: Decimal;
  taxTotal: Decimal;
  total: Decimal;
  currencyCode: IsoCurrencyCode;
}

function StaticTotalsFooter(props: StaticTotalsFooterProps) {
  return (
    <TotalsFooterShell
      subtotal={props.subtotal}
      taxTotal={props.taxTotal}
      total={props.total}
      currencyCode={props.currencyCode}
    />
  );
}

interface LiveTotalsFooterProps {
  control: Control<LineItemsFormState>;
  currencyCode: IsoCurrencyCode;
}

// Self-contained watcher for edit-mode totals. Isolating the useWatch
// subscription here keeps the parent component out of the keystroke-driven
// render loop — only this footer re-renders when line-item values change,
// and memoized rows stay untouched.
function LiveTotalsFooter(props: LiveTotalsFooterProps) {
  const watchedItems = useWatch({ control: props.control, name: 'lineItems' });
  const subtotal = watchedItems.reduce(
    (sum, li) => sum.plus(parseFormAmount(li.totalPrice.amount)),
    new CurrencyDecimal(0)
  );
  const taxTotal = watchedItems.reduce(
    (sum, li) => sum.plus(parseFormAmount(li.taxAmount.amount)),
    new CurrencyDecimal(0)
  );
  return (
    <TotalsFooterShell
      subtotal={subtotal}
      taxTotal={taxTotal}
      total={subtotal.plus(taxTotal)}
      currencyCode={props.currencyCode}
    />
  );
}

function TotalsFooterShell(props: StaticTotalsFooterProps) {
  return (
    <Stack alignItems="flex-end" sx={{ px: 2, py: 2 }}>
      <Stack sx={{ width: 240 }} spacing={1}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">
            Subtotal
          </Typography>
          <Typography variant="body2">
            {formatAmountWithCurrency(props.subtotal, props.currencyCode)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">
            Tax
          </Typography>
          <Typography variant="body2">
            {formatAmountWithCurrency(props.taxTotal, props.currencyCode)}
          </Typography>
        </Stack>
        <Divider />
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" fontWeight="bold">
            Total
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {formatAmountWithCurrency(props.total, props.currencyCode)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}
